// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';
import { Line2 } from 'three/addons/lines/Line2';
import { LineGeometry } from 'three/addons/lines/LineGeometry';

import { isNotNil } from 'helpers/isNotNil';
import type { HighlightableObjectName } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import {
  HighlightableLineGroup,
  type HighlightableLineType,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableLineGroup';

export type UnmarkedMeasurementLineGeometryOpts = {
  start: THREE.Vector3;
  end: THREE.Vector3;
  lineType: HighlightableLineType;
  /** Optional camera layer for the line. If not given, layer zero will be used, which is visible to all cameras */
  layer?: number;
  /** Optional render order for the line. If not given, the line will appear below other geometry. */
  renderOrder?: number;
};

export class UnmarkedMeasurementLineGeometry extends HighlightableLineGroup {
  protected readonly line: Line2;
  /** Start, end, and midpoints points in this group's local space */
  public readonly startLocal: THREE.Vector3;
  public readonly endLocal: THREE.Vector3;
  public readonly midpointLocal: THREE.Vector3 = new THREE.Vector3();
  protected readonly defaultRenderOrder: number;

  constructor(
    highlightableObjectName: HighlightableObjectName,
    { start, end, lineType, layer, renderOrder }: UnmarkedMeasurementLineGeometryOpts,
  ) {
    super(highlightableObjectName, lineType);

    this.startLocal = start.clone();
    this.endLocal = end.clone();
    this.midpointLocal = new THREE.Vector3().lerpVectors(start, end, 0.5);

    // Create dimension line
    const lineGeometry = new LineGeometry();
    lineGeometry.setFromPoints([start, end]);
    this.line = new Line2(lineGeometry, this.lineMaterial);
    this.line.computeLineDistances();
    if (isNotNil(renderOrder)) this.line.renderOrder = renderOrder;
    this.defaultRenderOrder = this.line.renderOrder;
    this.add(this.line);
    if (isNotNil(layer)) {
      this.layers.set(layer);
      this.line.layers.set(layer);
    }
  }

  highlight() {
    super.highlight();
    this.line.renderOrder = this.defaultRenderOrder + 1;
  }

  unhighlight() {
    super.unhighlight();
    this.line.renderOrder = this.defaultRenderOrder;
  }

  updateEndPoints(newStart: THREE.Vector3, newEnd: THREE.Vector3) {
    this.startLocal.copy(newStart);
    this.endLocal.copy(newEnd);
    this.midpointLocal.lerpVectors(newStart, newEnd, 0.5);

    this.line.geometry.setFromPoints([newStart, newEnd]);
    this.line.computeLineDistances();
  }

  hide() {
    this.visible = false;
  }

  unhide() {
    this.visible = true;
  }
}
